import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "../CrewPilotNavRow";
import { EstimateBottomSheet } from "../estimate/EstimateBottomSheet";
import { CPTextarea } from "../CPTextarea";
import {
  WorkspaceAddButton,
  WorkspaceEmpty,
} from "../workspace/WorkspaceChrome";
import { WorkspaceOverflowMenu } from "../workspace/WorkspaceOverflowMenu";
import { usePermissions } from "../../hooks/usePermissions";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { blockDemoSave } from "../../helpers/guestMode";
import { canAttemptSync, isOffline } from "../../helpers/connectivity";
import { mergePictureComments } from "../../helpers/projectFavorites";
import { loadProjectStructure } from "../../helpers/projectStructure";
import { safeUserErrorMessage } from "../../helpers/safeErrors";
import {
  carouselIndex,
  galleryContextForPicture,
  pictureSwipeDirection,
  picturesInGallery,
  stepCarousel,
} from "../../helpers/pictureCarousel";
import { createWebSpeechRecognitionAdapter } from "../../ashley/voice/recognition/WebSpeechRecognitionAdapter";
import {
  deleteLocalPicture,
  deleteLocalPictureNote,
  listCachedPictureComments,
  listCachedProjectPictures,
  readCachedPictureThumb,
  readLocalPictureBlob,
  readLocalPictureThumb,
  subscribePictureSync,
  updateLocalPicture,
} from "../../helpers/pictureSyncStore";
import {
  listPendingPictureNotes,
  listPendingProjectPictures,
  queuePictureNote,
  saveProjectPicture,
} from "../../helpers/pictureSync";
import {
  addProjectPictureComment,
  canVoidPictureComment,
  loadPictureAuthorNames,
  loadProjectPictureDetail,
  loadProjectPictures,
  pictureAuthorLabel,
  PICTURE_NOTE_PROMPT,
  reassignProjectPicture,
  shouldAskForPictureNote,
  suppressPictureNotePrompt,
  voidProjectPicture,
  voidProjectPictureComment,
} from "../../helpers/projectPictures";
import "./projectPictures.css";

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function activeComments(rows) {
  return (rows || [])
    .filter((row) => row && row.is_active !== false)
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
}

function locationName(locationId, locations, fallback = "Location") {
  if (!locationId) return "Overall Project";
  return (
    (locations || []).find((row) => row.project_location_id === locationId)?.name ||
    fallback
  );
}

export const PICTURE_LONG_PRESS_MS = 480;
const PICTURE_MOVE_CANCEL_PX = 10;

export function pictureGestureMoved(start, point) {
  if (!start || !point) return false;
  const dx = point.x - start.x;
  const dy = point.y - start.y;
  return dx * dx + dy * dy > PICTURE_MOVE_CANCEL_PX * PICTURE_MOVE_CANCEL_PX;
}

function pointerPoint(event) {
  const touch = event.touches?.[0] || event.changedTouches?.[0];
  if (touch) return { x: touch.clientX, y: touch.clientY };
  return { x: event.clientX, y: event.clientY };
}

/**
 * Selection actions stay in one list so a later pass can add Share, Download,
 * or Move without a second selection mode. This pass only offers Remove, and
 * only when the project is editable.
 */
export function buildPictureSelectionActions({ editable, onRemove }) {
  if (!editable || !onRemove) return [];
  return [{ id: "remove", label: "Remove", onSelect: onRemove }];
}

export function pictureRemovalCopy(rows) {
  const local = (rows || []).filter((row) => row?.local_only);
  const remote = (rows || []).length - local.length;
  if (local.length && !remote) {
    const noun = local.length === 1 ? "picture" : "pictures";
    return {
      title: `Remove ${local.length} ${noun} from this device?`,
      body: local.length === 1
        ? "This picture has not been uploaded. Removing it deletes it from this device."
        : "These pictures have not been uploaded. Removing them deletes them from this device.",
    };
  }
  if (local.length && remote) {
    return {
      title: `Remove ${rows.length} pictures?`,
      body: `${remote} will be removed from the project. ${local.length} have not been uploaded and will be deleted from this device.`,
    };
  }
  const count = (rows || []).length;
  const noun = count === 1 ? "picture" : "pictures";
  return {
    title: `Remove ${count} ${noun} from this project?`,
    body: "The pictures will no longer appear in the active project library.",
  };
}

function localPictureView(meta, previewUrl) {
  return {
    picture_id: meta.picture_id,
    project_id: meta.project_id,
    location_id: meta.location_id,
    file_name: meta.file_name,
    content_type: meta.content_type,
    created_by: meta.created_by,
    captured_at: meta.captured_at,
    created_at: meta.local_created_at,
    is_active: true,
    local_only: true,
    sync_state: meta.sync_state,
    attention_message: meta.attention_message || "",
    previewUrl,
  };
}

function PhotoTile({
  picture,
  locations,
  showLabel = false,
  selecting = false,
  selected = false,
  onPress,
  onLongPress,
}) {
  const timerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const movedRef = useRef(false);
  const originRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  function clearTimer() {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function onPointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    suppressClickRef.current = false;
    movedRef.current = false;
    originRef.current = pointerPoint(event);
    clearTimer();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is unavailable in some test environments.
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      suppressClickRef.current = true;
      onLongPress(picture);
    }, PICTURE_LONG_PRESS_MS);
  }

  function onPointerMove(event) {
    if (!originRef.current) return;
    if (pictureGestureMoved(originRef.current, pointerPoint(event))) {
      movedRef.current = true;
      clearTimer();
    }
  }

  function endPointer() {
    clearTimer();
    originRef.current = null;
    if (movedRef.current) suppressClickRef.current = true;
  }

  function onClick(event) {
    if (suppressClickRef.current || movedRef.current) {
      event.preventDefault();
      suppressClickRef.current = false;
      movedRef.current = false;
      return;
    }
    onPress(picture);
  }

  const label = locationName(picture.location_id, locations);
  return (
    <button
      type="button"
      className={selected ? "cp-photo-tile cp-photo-tile-selected" : "cp-photo-tile"}
      data-picture-id={picture.picture_id}
      aria-label={label}
      aria-pressed={selecting ? selected : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onContextMenu={(event) => event.preventDefault()}
      onDragStart={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {picture.previewUrl ? <img src={picture.previewUrl} alt="" draggable="false" /> : null}
      {picture.local_only ? (
        <span className="cp-photo-sync" data-testid="picture-sync-state">
          {picture.sync_state === "needs_attention" ? "Needs attention" : "Pending sync"}
        </span>
      ) : null}
      {showLabel ? <span className="cp-photo-tile-label">{label}</span> : null}
      {selecting ? (
        <span className={selected ? "cp-photo-check cp-photo-check-on" : "cp-photo-check"} aria-hidden="true">
          {selected ? "✓" : ""}
        </span>
      ) : null}
    </button>
  );
}

function PictureCarousel({ picture, rows, onStep }) {
  const originRef = useRef(null);
  const index = carouselIndex(rows, picture?.picture_id);
  const count = rows.length;
  const atStart = index <= 0;
  const atEnd = count === 0 || index >= count - 1;
  const src = picture?.imageUrl || picture?.previewUrl || "";

  function begin(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    originRef.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is unavailable in some test environments.
    }
  }

  function finish(event) {
    const origin = originRef.current;
    originRef.current = null;
    if (!origin || origin.pointerId !== event.pointerId) return;
    const direction = pictureSwipeDirection(origin, { x: event.clientX, y: event.clientY });
    if (direction) onStep(direction);
  }

  return (
    <div className="cp-photo-carousel" data-testid="picture-carousel">
      <div
        className="cp-photo-stage"
        data-testid="picture-carousel-stage"
        data-picture-id={picture?.picture_id || ""}
        onPointerDown={begin}
        onPointerUp={finish}
        onPointerCancel={() => {
          originRef.current = null;
        }}
      >
        {src ? <img src={src} alt="" draggable="false" /> : null}
      </div>
      <div className="cp-photo-carousel-bar">
        <button
          type="button"
          className="cp-photo-carousel-btn"
          onClick={() => onStep(-1)}
          disabled={atStart}
        >
          Previous
        </button>
        <div className="cp-photo-carousel-count" data-testid="picture-carousel-position">
          {count ? `${index + 1} of ${count}` : ""}
        </div>
        <button
          type="button"
          className="cp-photo-carousel-btn"
          onClick={() => onStep(1)}
          disabled={atEnd}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function PhotoGroup({ rows, locations, showLabel = false, selecting, selectedIds, onPress, onLongPress }) {
  if (!rows.length) {
    return <div className="cp-photo-group-empty">No pictures yet.</div>;
  }
  return (
    <div className="cp-photo-grid">
      {rows.map((picture) => (
        <PhotoTile
          key={picture.picture_id}
          picture={picture}
          locations={locations}
          showLabel={showLabel}
          selecting={selecting}
          selected={selectedIds.includes(picture.picture_id)}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      ))}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0" />
      <path d="M12 17v4" />
    </svg>
  );
}

/**
 * Master project photo library, grouped by Overall Project and each active
 * location, plus the filtered gallery used by a location workflow.
 * scope locks capture to one project location. Without scope, capture asks
 * where the photo belongs.
 */
export function ProjectPicturesScreen({
  projectId,
  projectName = "Project",
  editable = false,
  writable = editable,
  scope = null,
  onBack,
  onLibraryChanged,
  preview = null,
}) {
  const { actor } = usePermissions();
  const lockedLocationId = scope?.locationId || null;
  const [locations, setLocations] = useState(() => preview?.locations || []);
  const [pictures, setPictures] = useState(() => preview?.pictures || []);
  const [commentsById, setCommentsById] = useState(() => preview?.commentsById || {});
  const [authors, setAuthors] = useState(() => preview?.authors || {});
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");
  const [view, setView] = useState(() => {
    if (preview?.initialView) return preview.initialView;
    return lockedLocationId ? "location" : "hub";
  });
  const [activeLocationId, setActiveLocationId] = useState(
    () => preview?.locationId || lockedLocationId || "",
  );
  const [detailId, setDetailId] = useState(() => preview?.detailId || "");
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureStep, setCaptureStep] = useState("file");
  const [destinationId, setDestinationId] = useState("");
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [savingCount, setSavingCount] = useState(0);
  const [captureError, setCaptureError] = useState("");
  const [ashleyOpen, setAshleyOpen] = useState(Boolean(preview?.promptForNote));
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [voicePhase, setVoicePhase] = useState(preview?.voiceState || "");
  const [transcript, setTranscript] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const adapterRef = useRef(null);
  const transcriptRef = useRef("");
  const requestId = useRef(0);
  const actorRef = useRef(actor);
  const picturesRef = useRef(pictures);
  const detailIdRef = useRef(detailId);
  const annotationPictureIdRef = useRef("");
  const savingPhotoRef = useRef(false);
  const savingNoteRef = useRef(false);
  const captureOpenRef = useRef(false);
  const ashleyOpenRef = useRef(false);
  const voicePhaseRef = useRef("");
  const backStateRef = useRef({});
  const localUrlRef = useRef([]);
  const detailUrlRef = useRef("");
  actorRef.current = actor;
  picturesRef.current = pictures;
  detailIdRef.current = detailId;
  captureOpenRef.current = captureOpen;
  ashleyOpenRef.current = ashleyOpen;
  voicePhaseRef.current = voicePhase;
  backStateRef.current = { detailId, view, lockedLocationId, onBack };

  function navigationHeld() {
    return (
      savingPhotoRef.current ||
      captureOpenRef.current ||
      ashleyOpenRef.current ||
      Boolean(voicePhaseRef.current)
    );
  }

  const reloadPictures = useCallback(async () => {
    const request = ++requestId.current;
    const currentActor = actorRef.current;
    let tree = null;
    let remote = null;
    let remoteError = null;
    try {
      const [loadedTree, loadedRows] = await Promise.all([
        loadProjectStructure(currentActor, projectId),
        loadProjectPictures(currentActor, projectId),
      ]);
      tree = loadedTree || [];
      remote = loadedRows || [];
    } catch (error) {
      tree = null;
      remote = null;
      remoteError = error;
    }
    if (request !== requestId.current) return false;
    if (tree) setLocations(tree);

    let local = [];
    try {
      local = await listPendingProjectPictures(currentActor, projectId);
    } catch {
      local = [];
    }
    if (request !== requestId.current) return false;

    const urls = [];
    const localViews = [];
    const cachedViews = [];
    let cachedRows = [];
    try {
      cachedRows = await listCachedProjectPictures({
        userId: currentActor?.supabase_user_id,
        organizationId: currentActor?.organization_id,
        projectId,
      });
    } catch {
      cachedRows = [];
    }
    const seen = new Set([
      ...(remote || []).map((row) => row.picture_id),
      ...local.map((row) => row.picture_id),
    ]);
    for (const row of cachedRows) {
      if (!row?.picture_id || seen.has(row.picture_id)) continue;
      let previewUrl = "";
      try {
        const thumb = await readCachedPictureThumb(currentActor?.supabase_user_id, row.picture_id);
        if (thumb) {
          previewUrl = URL.createObjectURL(thumb);
          urls.push(previewUrl);
        }
      } catch {
        previewUrl = "";
      }
      cachedViews.push({
        ...row,
        previewUrl,
        local_only: false,
        cached: true,
        originalNeedsConnection: true,
      });
    }
    if (cachedRows.length) {
      setAuthors((current) => {
        const next = { ...current };
        cachedRows.forEach((row) => {
          if (row.created_by && row.author_name) next[row.created_by] = row.author_name;
        });
        return next;
      });
    }
    for (const meta of local) {
      if (remote?.some((row) => row.picture_id === meta.picture_id)) continue;
      let previewUrl = "";
      try {
        const thumb = await readLocalPictureThumb(meta.picture_id);
        if (thumb) {
          previewUrl = URL.createObjectURL(thumb);
          urls.push(previewUrl);
        }
      } catch {
        previewUrl = "";
      }
      localViews.push(localPictureView(meta, previewUrl));
    }
    if (request !== requestId.current) {
      urls.forEach((url) => URL.revokeObjectURL(url));
      return false;
    }
    localUrlRef.current.forEach((url) => URL.revokeObjectURL(url));
    localUrlRef.current = urls;
    if (remote) {
      setPictures([...localViews, ...cachedViews, ...remote]);
      return true;
    }
    setPictures((current) => [
      ...localViews,
      ...cachedViews,
      ...current.filter((row) => (
        !row.local_only
        && !row.cached
        && !localViews.some((view) => view.picture_id === row.picture_id)
        && !cachedViews.some((view) => view.picture_id === row.picture_id)
      )),
    ]);
    if (localViews.length || cachedViews.length) return true;
    if (remoteError) throw remoteError;
    return true;
  }, [projectId]);

  useEffect(() => {
    if (preview) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    reloadPictures()
      .catch((err) => {
        if (active) setError(safeUserErrorMessage(err, "Unable to load pictures."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      requestId.current += 1;
    };
  }, [preview, reloadPictures]);

  useEffect(() => () => {
    localUrlRef.current.forEach((url) => URL.revokeObjectURL(url));
    if (detailUrlRef.current) URL.revokeObjectURL(detailUrlRef.current);
    adapterRef.current?.abort?.();
  }, []);

  const refreshOpenComments = useCallback(async () => {
    const pictureId = detailIdRef.current;
    const currentActor = actorRef.current;
    if (!pictureId || preview) return;
    const picture = picturesRef.current?.find((row) => row.picture_id === pictureId);
    if (!picture || picture.local_only) return;
    try {
      const pending = await listPendingPictureNotes(currentActor, pictureId);
      if (isOffline()) {
        const cached = await listCachedPictureComments({
          userId: currentActor?.supabase_user_id,
          organizationId: currentActor?.organization_id,
          pictureId,
        });
        if (detailIdRef.current !== pictureId) return;
        setCommentsById((current) => ({
          ...current,
          [pictureId]: mergePictureComments(cached, pending),
        }));
        return;
      }
      const result = await loadProjectPictureDetail(currentActor, pictureId);
      if (detailIdRef.current !== pictureId) return;
      setCommentsById((current) => ({
        ...current,
        [pictureId]: mergePictureComments(result.comments, pending),
      }));
    } catch {
      // Keep the comments already on screen.
    }
  }, [preview]);

  useEffect(() => {
    if (preview) return undefined;
    return subscribePictureSync((event) => {
      if (savingPhotoRef.current || savingNoteRef.current) return;
      if (event?.type === "settled") {
        reloadPictures().catch(() => {});
      }
      if (event?.type === "update" || event?.type === "settled") {
        refreshOpenComments().catch(() => {});
      }
    });
  }, [preview, reloadPictures, refreshOpenComments]);

  const detail = pictures.find((row) => row.picture_id === detailId) || null;
  const detailComments = detail ? activeComments(commentsById[detail.picture_id]) : [];

  useEffect(() => {
    const id = actor?.supabase_user_id;
    const name = String(actor?.employee_name || "").trim();
    if (!id || !name) return;
    setAuthors((current) => (current[id] ? current : { ...current, [id]: name }));
  }, [actor?.supabase_user_id, actor?.employee_name]);

  async function rememberAuthors(userIds) {
    if (preview) return;
    try {
      const resolved = await loadPictureAuthorNames(actor, userIds);
      if (!Object.keys(resolved).length) return;
      setAuthors((current) => ({ ...current, ...resolved }));
    } catch {
      // The auth id remains on the row. The label falls back to Crew.
    }
  }

  const visiblePictures = pictures.filter((row) => {
    if (view === "overall") return !row.location_id;
    if (view === "location") return row.location_id === activeLocationId;
    return true;
  });
  const activeLocations = locations.filter((location) => location.is_active !== false);
  const selecting = selectedIds.length > 0;

  useEffect(() => {
    setSelectedIds((current) => {
      if (!current.length) return current;
      const live = new Set(pictures.map((row) => row.picture_id));
      const next = current.filter((id) => live.has(id));
      return next.length === current.length ? current : next;
    });
  }, [pictures]);

  function leaveNoteCheckpoint() {
    const pictureId = annotationPictureIdRef.current;
    setAshleyOpen(false);
    setVoicePhase("");
    setNote("");
    setNoteError("");
    updateTranscript("");
    if (!pictureId) return;
    annotationPictureIdRef.current = "";
    setDetailId("");
    if (lockedLocationId) {
      setView("location");
      return;
    }
    setActiveLocationId("");
    setView("hub");
  }

  function clearSelection() {
    setSelectedIds([]);
    setRemoveOpen(false);
  }

  function handleBack() {
    if (navigationHeld() || removing) return;
    if (removeOpen) {
      setRemoveOpen(false);
      return;
    }
    if (selecting) {
      clearSelection();
      return;
    }
    if (detailId) {
      setDetailId("");
      return;
    }
    onBack?.();
  }

  backStateRef.current.handleBack = handleBack;
  useHardwareBackHandler(
    useCallback(() => {
      if (
        savingPhotoRef.current ||
        captureOpenRef.current ||
        ashleyOpenRef.current ||
        Boolean(voicePhaseRef.current)
      ) {
        return true;
      }
      backStateRef.current.handleBack?.();
      return true;
    }, []),
  );

  function defaultDestination() {
    if (lockedLocationId) return lockedLocationId;
    if (view === "location" && activeLocationId) return activeLocationId;
    return "";
  }

  function openCapture() {
    if (!editable) return;
    setCaptureError("");
    setDestinationId(defaultDestination());
    setCaptureStep(lockedLocationId ? "file" : "destination");
    setCaptureOpen(true);
  }

  async function storePicture(file, locationId, previewId) {
    if (preview) {
      const savedId = previewId;
      const objectUrl = URL.createObjectURL(file);
      const saved = {
        picture_id: savedId,
        project_id: projectId,
        location_id: locationId,
        file_name: file.name || "photo.jpg",
        created_at: new Date().toISOString(),
        captured_at: new Date().toISOString(),
        created_by: actor?.supabase_user_id || null,
        is_active: true,
        previewUrl: objectUrl,
        imageUrl: objectUrl,
      };
      setPictures((current) => [saved, ...current.filter((row) => row.picture_id !== saved.picture_id)]);
      return { savedId, attention: "" };
    }

    const inserted = await saveProjectPicture(actor, projectId, file, {
      locationId,
      locationName: locationName(locationId, locations),
      locations,
    });
    const savedId = inserted?.picture?.picture_id || "";
    if (!savedId) throw new Error("This picture was not saved.");
    return {
      savedId,
      attention: inserted?.attention ? inserted.message || "This picture needs attention." : "",
    };
  }

  async function refreshLibrary() {
    if (preview) return false;
    let failed = false;
    try {
      await reloadPictures();
    } catch (refreshErr) {
      failed = true;
      setError(safeUserErrorMessage(refreshErr, "Picture saved. The library did not refresh."));
    }
    try {
      await onLibraryChanged?.();
    } catch {
      failed = true;
      setError("Picture saved. The library did not refresh.");
    }
    return failed;
  }

  async function saveFile(file) {
    if (!file || !editable) return;
    if (blockDemoSave(actor)) return;
    savingPhotoRef.current = true;
    setSavingPhoto(true);
    setSavingCount(1);
    setCaptureError("");
    const locationId = lockedLocationId || destinationId || null;
    try {
      const stored = await storePicture(file, locationId, `local-${Date.now()}`);
      if (stored.attention) setError(stored.attention);
      annotationPictureIdRef.current = stored.savedId;
      setCaptureOpen(false);
      if (shouldAskForPictureNote()) setAshleyOpen(true);
      else leaveNoteCheckpoint();
      await refreshLibrary();
    } catch (err) {
      setCaptureError(safeUserErrorMessage(err, "This picture was not saved."));
    } finally {
      savingPhotoRef.current = false;
      setSavingPhoto(false);
      setSavingCount(0);
    }
  }

  async function saveFiles(files) {
    if (!files.length || !editable) return;
    if (blockDemoSave(actor)) return;
    savingPhotoRef.current = true;
    setSavingPhoto(true);
    setSavingCount(files.length);
    setCaptureError("");
    const locationId = lockedLocationId || destinationId || null;
    const failures = [];
    const savedIds = [];
    let attention = "";
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        try {
          const stored = await storePicture(
            file,
            locationId,
            `local-${Date.now()}-${index}`,
          );
          savedIds.push(stored.savedId);
          if (stored.attention) attention = stored.attention;
        } catch (err) {
          failures.push(safeUserErrorMessage(err, "This picture was not saved."));
        }
      }

      if (!savedIds.length) {
        setCaptureError(failures[0] || "These pictures were not saved.");
        return;
      }

      // A batch note would attach to only one photo. Leave notes for each picture.
      annotationPictureIdRef.current = "";
      setCaptureOpen(false);
      const refreshFailed = await refreshLibrary();
      if (!refreshFailed && failures.length) {
        setError("Some pictures were not saved.");
      } else if (!refreshFailed && attention) {
        setError(attention);
      }
    } finally {
      savingPhotoRef.current = false;
      setSavingPhoto(false);
      setSavingCount(0);
    }
  }

  function onFile(event) {
    const input = event.target;
    const files = Array.from(input.files || []);
    input.value = "";
    if (!files.length) return;
    if (input === libraryRef.current && files.length > 1) {
      saveFiles(files);
      return;
    }
    saveFile(files[0]);
  }

  function stepGallery(direction) {
    if (!detail) return;
    const context = galleryContextForPicture(detail, {
      view,
      lockedLocationId,
      activeLocationId,
    });
    const rows = picturesInGallery(pictures, context);
    const index = carouselIndex(rows, detail.picture_id);
    const next = rows[stepCarousel(index, rows.length, direction)];
    if (!next || next.picture_id === detail.picture_id) return;
    openDetail(next);
  }

  const galleryStepRef = useRef(stepGallery);
  galleryStepRef.current = stepGallery;

  useEffect(() => {
    if (!detailId) return undefined;
    function onKey(event) {
      const tag = String(event.target?.tagName || "");
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "ArrowRight") galleryStepRef.current(1);
      if (event.key === "ArrowLeft") galleryStepRef.current(-1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailId]);

  function handleTilePress(picture) {
    if (selectedIds.length) {
      setSelectedIds((current) =>
        current.includes(picture.picture_id)
          ? current.filter((id) => id !== picture.picture_id)
          : [...current, picture.picture_id],
      );
      return;
    }
    openDetail(picture);
  }

  function handleTileLongPress(picture) {
    setSelectedIds((current) =>
      current.includes(picture.picture_id) ? current : [...current, picture.picture_id],
    );
  }

  async function openDetail(picture) {
    setSelectedIds([]);
    setDetailId(picture.picture_id);
    setNote("");
    setNoteError("");
    if (preview || commentsById[picture.picture_id]) return;
    if (picture.local_only) {
      try {
        const blob = await readLocalPictureBlob(picture.picture_id);
        if (blob) {
          if (detailUrlRef.current) URL.revokeObjectURL(detailUrlRef.current);
          const url = URL.createObjectURL(blob);
          detailUrlRef.current = url;
          setPictures((current) =>
            current.map((row) =>
              row.picture_id === picture.picture_id ? { ...row, imageUrl: url } : row,
            ),
          );
        }
        const notes = await listPendingPictureNotes(actor, picture.picture_id);
        setCommentsById((current) => ({ ...current, [picture.picture_id]: notes }));
      } catch (err) {
        setNoteError(safeUserErrorMessage(err, "Unable to load this picture."));
      }
      return;
    }
    if (isOffline() || picture.cached) {
      try {
        const cached = await listCachedPictureComments({
          userId: actor?.supabase_user_id,
          organizationId: actor?.organization_id,
          pictureId: picture.picture_id,
        });
        const pending = await listPendingPictureNotes(actor, picture.picture_id);
        const comments = mergePictureComments(cached, pending);
        setAuthors((current) => {
          const next = { ...current };
          [picture, ...cached, ...pending].forEach((row) => {
            if (row?.created_by && row?.author_name) next[row.created_by] = row.author_name;
          });
          return next;
        });
        setPictures((current) =>
          current.map((row) =>
            row.picture_id === picture.picture_id
              ? { ...row, originalNeedsConnection: true, previewUrl: row.previewUrl }
              : row,
          ),
        );
        setCommentsById((current) => ({ ...current, [picture.picture_id]: comments }));
      } catch (err) {
        setNoteError(safeUserErrorMessage(err, "Unable to load this picture."));
      }
      return;
    }
    try {
      const result = await loadProjectPictureDetail(actor, picture.picture_id);
      const pending = await listPendingPictureNotes(actor, picture.picture_id);
      setPictures((current) =>
        current.map((row) =>
          row.picture_id === picture.picture_id
            ? { ...row, ...result.picture, previewUrl: row.previewUrl || row.imageUrl }
            : row,
        ),
      );
      setCommentsById((current) => ({
        ...current,
        [picture.picture_id]: mergePictureComments(result.comments, pending),
      }));
      await rememberAuthors([
        result.picture?.created_by,
        ...(result.comments || []).map((row) => row.created_by),
      ]);
    } catch (err) {
      setNoteError(safeUserErrorMessage(err, "Unable to load this picture."));
    }
  }

  function updateTranscript(value) {
    transcriptRef.current = value;
    setTranscript(value);
  }

  function notePictureId() {
    return detail?.picture_id || annotationPictureIdRef.current || "";
  }

  async function saveComment(body, source) {
    const pictureId = notePictureId();
    if (!pictureId || !editable || savingNoteRef.current) return;
    if (blockDemoSave(actor)) return;
    const trimmed = String(body || "").trim();
    if (!trimmed) {
      setNoteError("Enter a note.");
      return;
    }
    savingNoteRef.current = true;
    setSavingNote(true);
    setNoteError("");
    try {
      const target = pictures.find((row) => row.picture_id === pictureId);
      const queueLocally = !preview && (target?.local_only || !canAttemptSync());
      const saved = preview
        ? {
            comment_id: `local-note-${Date.now()}`,
            picture_id: pictureId,
            body: trimmed,
            source,
            created_at: new Date().toISOString(),
            created_by: actor?.supabase_user_id || "preview-user",
            is_active: true,
          }
        : queueLocally
          ? await queuePictureNote(actor, target || { picture_id: pictureId, project_id: projectId }, { body: trimmed, source })
          : await addProjectPictureComment(actor, pictureId, { body: trimmed, source });
      setCommentsById((current) => ({
        ...current,
        [pictureId]: [...(current[pictureId] || []).filter((row) => row.comment_id !== saved.comment_id), saved],
      }));
      await rememberAuthors([saved.created_by]);
      if (annotationPictureIdRef.current && !detailId) {
        leaveNoteCheckpoint();
      } else {
        setNote("");
        updateTranscript("");
        setVoicePhase("");
      }
    } catch (err) {
      setNoteError(safeUserErrorMessage(err, "This note was not saved."));
    } finally {
      savingNoteRef.current = false;
      setSavingNote(false);
    }
  }

  function startVoice() {
    if (!editable) return;
    if (!canAttemptSync()) {
      setAshleyOpen(false);
      setVoicePhase("offline");
      setVoiceError("");
      return;
    }
    setAshleyOpen(false);
    setVoiceError("");
    updateTranscript("");
    const adapter = createWebSpeechRecognitionAdapter();
    adapterRef.current = adapter;
    if (!adapter.isSupported()) {
      setVoicePhase("unsupported");
      return;
    }
    setVoicePhase("listening");
    adapter.onResult((text, isFinal) => {
      if (!isFinal || !text) return;
      updateTranscript(`${transcriptRef.current} ${text}`.trim());
    });
    adapter.onError((code) => {
      if (code === "not-allowed") {
        setVoiceError("Microphone permission is required.");
      }
    });
    adapter.onEnd(() => {
      const text = transcriptRef.current.trim();
      setVoicePhase(text ? "confirm" : "empty");
    });
    adapter.start({ continuous: true, interimResults: false, lang: "en-US" });
  }

  function stopVoice() {
    adapterRef.current?.stop?.();
  }

  function cancelVoice() {
    adapterRef.current?.abort?.();
    updateTranscript("");
    setVoicePhase("");
    setVoiceError("");
    if (annotationPictureIdRef.current && !detailId) setAshleyOpen(true);
  }

  async function movePicture(locationId) {
    if (!detail || !editable) return;
    if (blockDemoSave(actor)) return;
    setCaptureError("");
    try {
      if (!preview && detail.local_only) {
        const saved = await updateLocalPicture(detail.picture_id, { location_id: locationId || null });
        setPictures((current) =>
          current.map((row) =>
            row.picture_id === detail.picture_id
              ? { ...row, ...saved, location_id: locationId || null, previewUrl: row.previewUrl }
              : row,
          ),
        );
        setMoveOpen(false);
        return;
      }
      const saved = preview
        ? { ...detail, location_id: locationId || null }
        : await reassignProjectPicture(actor, detail.picture_id, {
            locationId: locationId || null,
            locations,
          });
      setPictures((current) =>
        current.map((row) =>
          row.picture_id === detail.picture_id ? { ...row, ...saved, previewUrl: row.previewUrl } : row,
        ),
      );
      setMoveOpen(false);
    } catch (err) {
      setCaptureError(safeUserErrorMessage(err, "This picture was not moved."));
    }
  }

  async function removeComment(comment) {
    if (!detail || !comment) return;
    if (!writable || !canVoidPictureComment(actor, comment)) return;
    if (!window.confirm("Remove this comment?")) return;
    if (blockDemoSave(actor)) return;
    try {
      if (!preview && (comment.pending_sync || comment.local_only)) {
        await deleteLocalPictureNote(comment.comment_id);
      } else if (!preview) {
        await voidProjectPictureComment(actor, comment.comment_id);
      }
      setCommentsById((current) => ({
        ...current,
        [detail.picture_id]: (current[detail.picture_id] || []).filter(
          (row) => row.comment_id !== comment.comment_id,
        ),
      }));
    } catch (err) {
      setNoteError(safeUserErrorMessage(err, "This comment was not removed."));
    }
  }

  async function removePicture() {
    if (!detail || !editable) return;
    const prompt = detail.local_only
      ? "Remove this picture from this device? It has not been uploaded."
      : "Remove this picture from the project?";
    if (!window.confirm(prompt)) return;
    if (blockDemoSave(actor)) return;
    try {
      if (!preview && detail.local_only) await deleteLocalPicture(detail.picture_id);
      else if (!preview) await voidProjectPicture(actor, detail.picture_id);
      setPictures((current) => current.filter((row) => row.picture_id !== detail.picture_id));
      setDetailId("");
    } catch (err) {
      setNoteError(safeUserErrorMessage(err, "This picture was not removed."));
    }
  }

  async function confirmBulkRemove() {
    if (!editable || removing) return;
    const ids = selectedIds.slice();
    if (!ids.length) return;
    if (blockDemoSave(actor)) return;
    setRemoving(true);
    setError("");
    const removed = [];
    let failure = null;
    const selectedRows = pictures.filter((row) => ids.includes(row.picture_id));
    try {
      for (const pictureId of ids) {
        const row = selectedRows.find((item) => item.picture_id === pictureId);
        try {
          if (!preview && row?.local_only) await deleteLocalPicture(pictureId);
          else if (!preview) await voidProjectPicture(actor, pictureId);
          removed.push(pictureId);
        } catch (err) {
          failure = err;
          break;
        }
      }
      if (removed.length) {
        const removedSet = new Set(removed);
        setPictures((current) => current.filter((row) => !removedSet.has(row.picture_id)));
        setSelectedIds((current) => current.filter((id) => !removedSet.has(id)));
      }
      if (failure) {
        setError(safeUserErrorMessage(failure, "Some pictures were not removed."));
      } else {
        setRemoveOpen(false);
      }
      if (removed.length && !preview) {
        try {
          await reloadPictures();
        } catch (refreshErr) {
          setError(safeUserErrorMessage(refreshErr, "Pictures removed. The library did not refresh."));
        }
        try {
          await onLibraryChanged?.();
        } catch {
          setError("Pictures removed. The library did not refresh.");
        }
      }
    } finally {
      setRemoving(false);
    }
  }

  const shell = selecting
    ? { title: `${selectedIds.length} selected`, section: "", titleClassName: "cp-photo-select-title" }
    : detail
      ? { title: "Photo", section: locationName(detail.location_id, locations) }
      : view === "all"
        ? { title: "All Photos", section: "Pictures" }
        : view === "overall"
          ? { title: "Overall Project", section: "Pictures" }
          : view === "location"
            ? {
                title: "Pictures",
                section: locationName(activeLocationId, locations, scope?.locationName),
              }
            : { title: "Pictures", section: "Project" };

  const showBadges = view === "all";
  const selectionActions = buildPictureSelectionActions({
    editable,
    onRemove: () => setRemoveOpen(true),
  });

  return (
    <CrewPilotModuleShell
      title={shell.title}
      sectionLabel={shell.section}
      titleClassName={shell.titleClassName || ""}
      backLabel={selecting ? "Cancel" : detail ? "Close" : "Back"}
      onBack={handleBack}
      headerActions={
        selecting
          ? selectionActions.map((action) => (
              <button
                key={action.id}
                type="button"
                className="cp-photo-select-action"
                onClick={action.onSelect}
              >
                {action.label}
              </button>
            ))
          : null
      }
    >
      {error && <div className="est-inline-error">{error}</div>}
      {loading && <WorkspaceEmpty>Loading pictures…</WorkspaceEmpty>}

      {!loading && !detail && view === "hub" && (
        <div data-testid="project-pictures-library">
          <CrewPilotNavSectionHeader>Pictures</CrewPilotNavSectionHeader>
          <CrewPilotNavSectionHeader>Overall Project</CrewPilotNavSectionHeader>
          <PhotoGroup
            rows={pictures.filter((row) => !row.location_id)}
            locations={locations}
            selecting={selecting}
            selectedIds={selectedIds}
            onPress={handleTilePress}
            onLongPress={handleTileLongPress}
          />
          <CrewPilotNavSectionHeader>Locations</CrewPilotNavSectionHeader>
          {activeLocations.length === 0 ? (
            <WorkspaceEmpty>No locations yet.</WorkspaceEmpty>
          ) : (
            activeLocations.map((location) => (
                <div key={location.project_location_id}>
                  <div className="cp-photo-place">{location.name}</div>
                  <PhotoGroup
                    rows={pictures.filter((row) => row.location_id === location.project_location_id)}
                    locations={locations}
                    selecting={selecting}
                    selectedIds={selectedIds}
                    onPress={handleTilePress}
                    onLongPress={handleTileLongPress}
                  />
                </div>
              ))
          )}
          {editable && !selecting && (
            <WorkspaceAddButton onClick={openCapture}>+ Add Picture</WorkspaceAddButton>
          )}
        </div>
      )}

      {!loading && !detail && view !== "hub" && (
        <div data-testid="project-pictures-gallery">
          {visiblePictures.length === 0 ? (
            <WorkspaceEmpty>No pictures yet.</WorkspaceEmpty>
          ) : (
            <PhotoGroup
              rows={visiblePictures}
              locations={locations}
              showLabel={showBadges}
              selecting={selecting}
              selectedIds={selectedIds}
              onPress={handleTilePress}
              onLongPress={handleTileLongPress}
            />
          )}
          {editable && !selecting && (
            <WorkspaceAddButton onClick={openCapture}>+ Add Picture</WorkspaceAddButton>
          )}
        </div>
      )}

      {!loading && detail && (
        <div className="cp-picture-detail" data-testid="project-picture-detail">
          <PictureCarousel
            picture={detail}
            rows={picturesInGallery(
              pictures,
              galleryContextForPicture(detail, { view, lockedLocationId, activeLocationId }),
            )}
            onStep={stepGallery}
          />
          <div className="cp-photo-meta">
            <div className="cp-photo-meta-project">{projectName}</div>
            <div className="cp-photo-meta-place">{locationName(detail.location_id, locations)}</div>
            {detail.originalNeedsConnection && !detail.local_only ? (
              <div className="cp-photo-meta-by" data-testid="picture-original-offline">
                Full-resolution image requires connection
              </div>
            ) : null}
            {detail.captured_at && (
              <div className="cp-photo-meta-time">Captured {formatWhen(detail.captured_at)}</div>
            )}
            {detail.local_only ? (
              <>
                <div className="cp-photo-meta-by">
                  Captured by {pictureAuthorLabel(detail.created_by, authors)}
                </div>
                <div className="cp-photo-meta-by" data-testid="picture-sync-detail">
                  {detail.sync_state === "needs_attention"
                    ? `Needs attention. ${detail.attention_message || "This project is no longer writable."}`
                    : "Saved on device · Pending sync"}
                </div>
              </>
            ) : (
              <div className="cp-photo-meta-by">
                Uploaded by {pictureAuthorLabel(detail.created_by, authors)}
                {detail.created_at ? (
                  <span className="cp-photo-meta-time"> · {formatWhen(detail.created_at)}</span>
                ) : null}
              </div>
            )}
          </div>

          <CrewPilotNavSectionHeader>Notes & Comments</CrewPilotNavSectionHeader>
          <div className="cp-photo-comments" data-testid="project-picture-comments">
            {detailComments.length === 0 && (
              <WorkspaceEmpty>No notes yet.</WorkspaceEmpty>
            )}
            {detailComments.map((comment) => (
              <div key={comment.comment_id} className="cp-photo-comment">
                <div className="cp-photo-comment-head">
                  <div className="cp-photo-comment-by">
                    <span className="cp-photo-comment-author">
                      {pictureAuthorLabel(comment.created_by, authors)}
                    </span>
                    <span className="cp-photo-comment-time">
                      {" "}· {formatWhen(comment.created_at)}
                      {comment.pending_sync ? " · Pending sync" : ""}
                    </span>
                  </div>
                  {writable && canVoidPictureComment(actor, comment) && (
                    <WorkspaceOverflowMenu
                      ariaLabel="Comment actions"
                      items={[
                        {
                          label: "Remove comment",
                          destructive: true,
                          onClick: () => removeComment(comment),
                        },
                      ]}
                    />
                  )}
                </div>
                <div className="cp-photo-comment-body">{comment.body}</div>
              </div>
            ))}
          </div>

          {editable && (
            <>
              <button type="button" className="cp-voice-note-btn" onClick={startVoice}>
                <MicIcon />
                Add Voice Note
              </button>
              <div className="cp-photo-note-box">
                <CPTextarea
                  label="Type a note"
                  value={note}
                  onChange={setNote}
                  placeholder="Type a note…"
                  rows={3}
                />
                <WorkspaceAddButton onClick={() => saveComment(note, "typed")}>
                  {savingNote ? "Saving…" : "Save note"}
                </WorkspaceAddButton>
              </div>
              {noteError && <div className="est-inline-error">{noteError}</div>}
              <WorkspaceAddButton onClick={() => setMoveOpen(true)}>Move</WorkspaceAddButton>
              <WorkspaceAddButton onClick={removePicture}>Remove from project</WorkspaceAddButton>
            </>
          )}
        </div>
      )}

      <div className="est-bottom-spacer" />

      {captureOpen && captureStep === "destination" && (
        <EstimateBottomSheet
          title="Save to"
          onClose={() => setCaptureOpen(false)}
          secondaryLabel="Cancel"
          primaryLabel="Continue"
          onPrimary={() => setCaptureStep("file")}
        >
          <div className="cp-photo-dest-list">
            <CrewPilotNavList>
              <CrewPilotNavRow
                title="Overall Project"
                subtitle={destinationId ? "" : "Selected"}
                onClick={() => setDestinationId("")}
              />
              {locations.map((location) => (
                <CrewPilotNavRow
                  key={location.project_location_id}
                  title={location.name}
                  subtitle={destinationId === location.project_location_id ? "Selected" : ""}
                  onClick={() => setDestinationId(location.project_location_id)}
                />
              ))}
            </CrewPilotNavList>
          </div>
        </EstimateBottomSheet>
      )}

      {captureOpen && captureStep === "file" && (
        <EstimateBottomSheet
          title="Add Picture"
          subtitle={locationName(lockedLocationId || destinationId, locations)}
          onClose={() => {
            if (savingPhotoRef.current) return;
            setCaptureOpen(false);
          }}
          secondaryLabel="Cancel"
          primaryBusy={savingPhoto}
          error={captureError}
        >
          <div className="cp-photo-capture-choice">
            <WorkspaceAddButton onClick={() => cameraRef.current?.click()}>
              Take Photo
            </WorkspaceAddButton>
            <WorkspaceAddButton onClick={() => libraryRef.current?.click()}>
              Choose Existing Photo
            </WorkspaceAddButton>
            {savingPhoto && (
              <div className="cp-photo-status">
                {savingCount > 1 ? `Saving ${savingCount} photos…` : "Saving photo…"}
              </div>
            )}
          </div>
        </EstimateBottomSheet>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onFile}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
        multiple
        hidden
        onChange={onFile}
      />

      {ashleyOpen && (
        <EstimateBottomSheet
          title={PICTURE_NOTE_PROMPT}
          onClose={() => {}}
          secondaryLabel="Not now"
          onSecondary={leaveNoteCheckpoint}
          primaryLabel="Add Voice Note"
          onPrimary={startVoice}
        >
          <CPTextarea
            label="Type a note"
            value={note}
            onChange={setNote}
            placeholder="Type a note…"
            rows={3}
          />
          <WorkspaceAddButton onClick={() => saveComment(note, "typed")}>
            {savingNote ? "Saving…" : "Save note"}
          </WorkspaceAddButton>
          {noteError && <div className="est-inline-error">{noteError}</div>}
          <button
            type="button"
            className="cp-voice-note-btn"
            onClick={() => {
              suppressPictureNotePrompt();
              leaveNoteCheckpoint();
            }}
          >
            Don't ask again this session
          </button>
        </EstimateBottomSheet>
      )}

      {voicePhase === "listening" && (
        <EstimateBottomSheet
          title="Listening"
          subtitle="Add Voice Note"
          onClose={cancelVoice}
          secondaryLabel="Cancel"
          onSecondary={cancelVoice}
          primaryLabel="Stop"
          onPrimary={stopVoice}
          error={voiceError}
        >
          <div className="cp-photo-status">Listening…</div>
        </EstimateBottomSheet>
      )}

      {voicePhase === "confirm" && (
        <EstimateBottomSheet
          title="Voice note"
          onClose={cancelVoice}
          secondaryLabel="Discard"
          onSecondary={cancelVoice}
          primaryLabel="Save note"
          primaryBusy={savingNote}
          onPrimary={() => saveComment(transcriptRef.current, "voice")}
          error={noteError}
        >
          <CPTextarea label="Transcript" value={transcript} onChange={updateTranscript} rows={4} />
        </EstimateBottomSheet>
      )}

      {voicePhase === "empty" && (
        <EstimateBottomSheet
          title="No speech heard"
          onClose={cancelVoice}
          secondaryLabel="Cancel"
          onSecondary={cancelVoice}
          primaryLabel="Try again"
          onPrimary={startVoice}
        >
          <div className="cp-photo-status">The picture is still saved.</div>
        </EstimateBottomSheet>
      )}

      {voicePhase === "offline" && (
        <EstimateBottomSheet
          title="Voice note"
          onClose={cancelVoice}
          secondaryLabel="Close"
          onSecondary={cancelVoice}
        >
          <div className="cp-photo-status">
            Voice notes need a connection. Type a note or skip. This picture stays saved on this device.
          </div>
        </EstimateBottomSheet>
      )}

      {voicePhase === "unsupported" && (
        <EstimateBottomSheet
          title="Voice note"
          onClose={cancelVoice}
          secondaryLabel="Close"
          onSecondary={cancelVoice}
        >
          <div className="cp-photo-status">
            Voice notes need the microphone on this device. Type a note instead.
          </div>
        </EstimateBottomSheet>
      )}

      {removeOpen && selecting && (
        <EstimateBottomSheet
          title={pictureRemovalCopy(pictures.filter((row) => selectedIds.includes(row.picture_id))).title}
          onClose={() => {
            if (removing) return;
            setRemoveOpen(false);
          }}
          secondaryLabel="Cancel"
          primaryLabel={removing ? "Removing…" : "Remove Pictures"}
          primaryDisabled={removing}
          onPrimary={confirmBulkRemove}
        >
          <p className="cp-photo-remove-copy">
            {pictureRemovalCopy(pictures.filter((row) => selectedIds.includes(row.picture_id))).body}
          </p>
        </EstimateBottomSheet>
      )}

      {moveOpen && detail && (
        <EstimateBottomSheet
          title="Move picture"
          onClose={() => setMoveOpen(false)}
          secondaryLabel="Cancel"
          error={captureError}
        >
          <CrewPilotNavList>
            <CrewPilotNavRow title="Overall Project" onClick={() => movePicture(null)} />
            {locations.map((location) => (
              <CrewPilotNavRow
                key={location.project_location_id}
                title={location.name}
                onClick={() => movePicture(location.project_location_id)}
              />
            ))}
          </CrewPilotNavList>
        </EstimateBottomSheet>
      )}
    </CrewPilotModuleShell>
  );
}
